import { graphqlFetch } from '@pylot-data/graphqlFetch'
import type { CustomerToken, Cart, Customer } from '@pylot-data/fwrdschema'
import { GraphQLError } from '@pylot-data/graphqlFetch'
import { mergeCarts, refreshCart } from '@corratech/pylot-cart-manager'
import { useAccount } from '@corratech/pylot-auth-manager'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { useState } from 'react'
import { loginEvent } from '@lib/utils/exponeaTrackingEvents'
import { useAuthUI } from '@lib/casaideas-components/pylot-auth-manager'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
export type GenerateTokenResponse = {
    data: CustomerToken
}

export type LoginArguments = {
    credential: string
    websiteId: number
}
interface FetchAccountInfoResult {
    data?: {
        customer?: Customer
    }
    errors?: GraphQLError[]
}

interface MergeCartsResult {
    data?: {
        cart: Cart
    }
    errors?: GraphQLError[]
}

type ResultType = [FetchAccountInfoResult, MergeCartsResult]

export const useGoogleLogin = (
    updateCart: (res: any, isOptimistic: boolean) => void
) => {
    const { mutate: fetchAccountInfo } = useAccount()
    const router = useRouter()
    const { locale } = router
    const { t } = useTranslation(['account'])
    const { loginCallbacks, clearAuthCallback } = useAuthUI()
    const { openToast } = useUI()
    const [isLoading, setIsLoading] = useState(false)

    return {
        googleLogin: async (params: LoginArguments) => {
            setIsLoading(true)
            const res = await graphqlFetch<
                LoginArguments,
                { onetapIntegrateLogin: CustomerToken }
            >({
                query: getTokenMutation,
                variables: { ...params },
                locale
            })
            if (res.errors) {
                setIsLoading(false)
                openToast(
                    t('account||Something went wrong. Please try again'),
                    ToastType.Danger,
                    true
                )
                throw res.errors
            }
            localStorage.setItem(
                'pylot_token',
                res.data!.onetapIntegrateLogin.token!
            )
            const guestCartId = localStorage.getItem('cart_id')
            const result: ResultType = (await Promise.all([
                fetchAccountInfo(),
                // Sin carro de invitado no hay nada que fusionar: se trae el del
                // cliente, que también trae el email para loginEvent.
                guestCartId
                    ? mergeCarts({
                          guestCartId,
                          locale
                      }).then((res) => {
                          updateCart(res, false)
                          return res
                      })
                    : refreshCart()
            ])) as [FetchAccountInfoResult, MergeCartsResult]

            if (result) {
                const email = result[1]?.data?.cart?.email
                if (email) {
                    loginEvent({ email: email })
                }
            }
            setIsLoading(false)
            //handle login callbacks
            if (loginCallbacks.length) {
                loginCallbacks.forEach((callback) => {
                    if (typeof callback === 'function') {
                        callback()
                    }
                })
                clearAuthCallback('LOGIN_VIEW')
            } else {
                setTimeout(() => {
                    window.dispatchEvent(
                        new CustomEvent('pylot:refresh-content')
                    )
                }, 0)
            }
            return result
        },
        isLoading
    }
}

const getTokenMutation = /* GraphQL */ `
    mutation onetapIntegrateLogin($credential: String!, $websiteId: Int!) {
        onetapIntegrateLogin(credential: $credential, websiteId: $websiteId) {
            token
        }
    }
`
